import { useParams } from 'react-router-dom'
import { SettingsPage } from 'Features/Projects/Scenarios/EditSettings/SettingsPage'
import {
  useBaseSourceSiteWorkCentre,
  useGenerateTemplates,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.data'
import { Loader } from 'Common/Loader/Loader'
import { PlanTemplateSummaryTable } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.Table'
import { GridRowId } from '@mui/x-data-grid-premium'
import {
  BaseSourceSiteWorkCentre,
  RUN_METHODS,
  Status,
  STATUS_MAP,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

export const PlanTemplateSummaryView = () => {
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>() as { projectId: string; scenarioId: string }

  const { data, isLoading, refetch, isRefetching } =
    useBaseSourceSiteWorkCentre({
      projectId,
      scenarioId,
    })

  const [dataState, setDataState] = useState<BaseSourceSiteWorkCentre[]>([])

  useEffect(() => {
    if (data) {
      setDataState(data)
    }
  }, [data])

  const { mutate: generate, isPending: isTemplateGenerating } =
    useGenerateTemplates(
      () => {
        // The run has been queued, not finished; the status columns show when
        // each method's template completes.
        toast.success(
          `Template generation started: ${RUN_METHODS.length} methods per work centre`
        )
      },
      () => {
        toast.error('Templates Generation Failed')
      }
    )

  // Every selected work centre is solved with each of RUN_METHODS. The API
  // marks their statuses 'initiated' once the run has started; they show as
  // initiated here straight away. (The page used to save the statuses itself,
  // by posting the whole grid, so a refused or failed start left them
  // initiated for good and an old copy of the grid could undo newer ones.)
  const handleRunTemplates = (selectedRows: GridRowId[]) => {
    const selected = dataState.filter((setting) =>
      selectedRows.includes(setting.id)
    )
    if (selected.length === 0) return

    const initiated = Object.fromEntries(
      RUN_METHODS.map((method) => [STATUS_MAP[method], 'initiated' as Status])
    )
    const newState: BaseSourceSiteWorkCentre[] = dataState.map((setting) =>
      selectedRows.includes(setting.id) ? { ...setting, ...initiated } : setting
    )

    setDataState(newState)
    generate({
      projectId,
      scenarioId,
      workcentreIds: selected.map((setting) => setting.workCentreKey),
    })
  }

  const handleRefresh = () => {
    refetch()
  }

  return (
    <SettingsPage title="Template summary">
      {isLoading && <Loader />}
      {data && (
        <PlanTemplateSummaryTable
          data={dataState}
          handleRunTemplates={handleRunTemplates}
          isTemplateGenerating={isTemplateGenerating}
          handleRefresh={handleRefresh}
          isRefetching={isRefetching}
        />
      )}
    </SettingsPage>
  )
}
